import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useTheme } from "@/contexts/ThemeContext";
import {
  LayoutDashboard, Brain, Users, Grid3X3, Bell,
  Target, BarChart3, TrendingUp, MessageSquare,
  GanttChart, Settings, Sun, Moon, LogOut, Lock,
  FileCode2, ScanLine
} from "lucide-react";

const NAV_ITEMS = [
  { label: "PRIMARY", type: "section" },
  { path: "/executive/demo/dashboard", label: "Command Center", icon: LayoutDashboard },
  { path: "/executive/demo/intelligence", label: "Live Intelligence", icon: Brain },
  { path: "/executive/demo/people", label: "People", icon: Users },
  { path: "/executive/demo/heatmap", label: "Workforce Heatmap", icon: Grid3X3 },
  { path: "/executive/demo/alerts", label: "Alerts", icon: Bell, badge: true },
  { path: "/executive/demo/technical-assessment", label: "Technical Assessment", icon: FileCode2 },
  { path: "/executive/xray-cohort", label: "Assessment Results", icon: ScanLine },
  { label: "STRATEGIC (UNLOCKS)", type: "section" },
  { path: "/executive/demo/talent-strategy", label: "Talent Strategy", icon: Target, locked: true, unlock: "75% SA" },
  { path: "/executive/demo/workforce-planning", label: "Workforce Planning", icon: BarChart3, locked: true, unlock: "50% MV" },
  { path: "/executive/demo/roi", label: "ROI Analysis", icon: TrendingUp, locked: true, unlock: "100%" },
  { label: "UTILITY", type: "section" },
  { path: "/executive/demo/settings", label: "Settings", icon: Settings },
];

export default function DemoSidebar({ alertCount = 0 }) {
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useState({ fullName: "", email: "" });

  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("userInfo") || "{}");
      setUserInfo({ fullName: stored.fullName || "Executive", email: stored.email || "" });
    } catch { setUserInfo({ fullName: "Executive", email: "" }); }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("executiveToken");
    localStorage.removeItem("userRole");
    localStorage.removeItem("userInfo");
    navigate("/executive");
  };

  return (
    <aside
      className="fixed left-0 top-0 h-screen flex flex-col z-50"
      style={{
        width: 260,
        background: "var(--surface-card)",
        borderRight: "var(--border-width) solid var(--border-default)",
      }}
      data-testid="demo-sidebar"
    >
      {/* Logo */}
      <div className="px-5 py-5 border-b" style={{ borderColor: "var(--border-default)" }}>
        <div className="flex items-center gap-2.5">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-sm font-bold"
            style={{ background: "var(--color-accent)" }}
          >
            S
          </div>
          <div>
            <div className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>
              Workforce Intelligence
            </div>
            <div className="text-[10px]" style={{ color: "var(--text-muted)" }}>
              Sagicor TCA
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-3 px-2">
        {NAV_ITEMS.map((item, i) => {
          if (item.type === "section") {
            return (
              <div
                key={i}
                className="px-3 pt-5 pb-1.5 text-[10px] font-semibold tracking-wider"
                style={{ color: "var(--text-muted)" }}
              >
                {item.label}
              </div>
            );
          }

          const Icon = item.icon;
          return (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 rounded-md text-[13px] transition-all mb-0.5 ${
                  item.locked ? "opacity-50 cursor-not-allowed" : ""
                }`
              }
              style={({ isActive }) => ({
                background: isActive ? "var(--color-accent-bg)" : "transparent",
                color: isActive
                  ? "var(--color-accent)"
                  : item.locked
                  ? "var(--text-muted)"
                  : "var(--text-secondary)",
                fontWeight: isActive ? 500 : 400,
              })}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {item.locked ? (
                <Lock className="w-4 h-4 flex-shrink-0" />
              ) : (
                <Icon className="w-4 h-4 flex-shrink-0" />
              )}
              <span className="flex-1 truncate">{item.label}</span>
              {item.badge && alertCount > 0 && (
                <span
                  className="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                  style={{
                    background: "var(--color-danger-bg)",
                    color: "var(--color-danger)",
                  }}
                >
                  {alertCount}
                </span>
              )}
              {item.locked && (
                <span className="text-[10px]" style={{ color: "var(--text-muted)" }}>
                  {item.unlock}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* Theme Toggle */}
      <div className="px-4 py-3 border-t" style={{ borderColor: "var(--border-default)" }}>
        <button
          onClick={toggleTheme}
          className="flex items-center gap-2 w-full px-3 py-2 rounded-md text-[13px] transition-all"
          style={{
            background: "var(--surface-muted)",
            color: "var(--text-secondary)",
          }}
          data-testid="theme-toggle"
        >
          {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          <span>{isDark ? "Light Mode" : "Dark Mode"}</span>
        </button>
      </div>

      {/* User */}
      <div
        className="px-4 py-3 border-t flex items-center justify-between"
        style={{ borderColor: "var(--border-default)" }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0"
            style={{ background: "var(--color-accent-bg)", color: "var(--color-accent)" }}
          >
            {userInfo.fullName.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="text-[12px] font-medium truncate" style={{ color: "var(--text-primary)" }}>
              {userInfo.fullName}
            </div>
            <div className="text-[10px] truncate" style={{ color: "var(--text-muted)" }}>
              {userInfo.email}
            </div>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="p-1.5 rounded-md transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4" style={{ color: "var(--text-muted)" }} />
        </button>
      </div>
    </aside>
  );
}
